import { useMemo, useState, useCallback } from 'react';
import { Form, message } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getProjectCollaboration,
    inviteProjectCollaboration,
    inviteProjectCollaborationMember,
    acceptProjectCollaboration,
    rejectProjectCollaboration,
} from '@services/projects.service';
import { useCanCheck } from '@/ability/helpers';
import {
    PROJECT_COLLABORATION_VIEW,
    PROJECT_COLLABORATION_INVITE,
    PROJECT_COLLABORATION_ASSIGN,
} from '@/ability';
import { useAuthStore } from '@stores/auth.store';
import { useUsers } from '@hooks/users/useUsers';
import { formatDate } from '@hooks/projects/projectHelpers';
import { invalidateProjectQueries } from './projectQueryInvalidation';
import { useProjectDetail } from '@hooks/projects/detail/useProjectDetail';

export const PROJECT_COLLABORATION_QUERY_KEY = (projectId) => ['projects', projectId, 'collaboration'];

function normalizeCollaborationRow(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const id = raw.id ?? raw._id ?? raw.collaborationId ?? raw.collaboration_id;
    const invitedTenantId =
        raw.invitedTenantId ?? raw.invited_tenant_id ?? raw.tenantId ?? raw.tenant_id ?? raw.companyId ?? raw.company_id;
    const tenantName =
        raw.tenantName ??
        raw.tenant_name ??
        raw.companyName ??
        raw.company_name ??
        raw.invitedTenantName ??
        raw.name ??
        invitedTenantId ??
        '—';
    const status = String(raw.status ?? 'pending').toLowerCase();
    const members = Array.isArray(raw.members)
        ? raw.members
        : Array.isArray(raw.assignedUsers)
          ? raw.assignedUsers
          : [];
    return {
        id,
        invitedTenantId,
        tenantName: String(tenantName).trim() || '—',
        status,
        members,
        memberCount: members.length,
        invitedAt: raw.invitedAt ?? raw.invited_at ?? raw.createdAt ?? raw.created_at,
        raw,
    };
}

function buildInviteTenantPayload(values) {
    const tenantId = String(values?.tenantId ?? values?.companyId ?? '').trim();
    if (!tenantId) return null;
    return { tenantId, companyId: tenantId };
}

/**
 * Project collaboration tab: cross-tenant invites, accept/reject, assign members.
 * RBAC: projects.collaboration.view | .invite | .assign
 */
export function useProjectCollaboration(projectId) {
    const queryClient = useQueryClient();
    const can = useCanCheck();
    const canView = can(PROJECT_COLLABORATION_VIEW);
    const canInvite = can(PROJECT_COLLABORATION_INVITE);
    const canAssign = can(PROJECT_COLLABORATION_ASSIGN);

    const user = useAuthStore((s) => s.user);
    const currentTenantId =
        user?.tenantId ?? (Array.isArray(user?.tenants) ? user?.tenants?.[0]?.id ?? null : null);

    const [inviteTenantModalOpen, setInviteTenantModalOpen] = useState(false);
    const [inviteTenantForm] = Form.useForm();
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [assignTarget, setAssignTarget] = useState(null);
    const [assignForm] = Form.useForm();

    const { data: project } = useProjectDetail(projectId, { enabled: !!projectId });
    const isCollaborationEnabled = project?.modules?.isCollaboration?.enabled !== false;

    const {
        data: collaborationsRaw = [],
        isLoading,
        isError,
        refetch,
    } = useQuery({
        queryKey: PROJECT_COLLABORATION_QUERY_KEY(projectId),
        queryFn: () => getProjectCollaboration(projectId),
        enabled: !!projectId && canView && isCollaborationEnabled,
    });

    const collaborations = useMemo(() => {
        const list = Array.isArray(collaborationsRaw) ? collaborationsRaw : [];
        return list.map(normalizeCollaborationRow).filter(Boolean);
    }, [collaborationsRaw]);

    const { data: usersData } = useUsers(
        { page: 1, limit: 100 },
        { enabled: !!projectId && assignModalOpen && canAssign },
    );
    const usersList = usersData?.data ?? [];

    const invalidateCollaboration = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: PROJECT_COLLABORATION_QUERY_KEY(projectId) });
        invalidateProjectQueries(queryClient, projectId);
    }, [queryClient, projectId]);

    const inviteTenantMutation = useMutation({
        mutationFn: (body) => inviteProjectCollaboration(projectId, body),
        onSuccess: () => {
            invalidateCollaboration();
            message.success('Collaborator tenant invited.');
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || err?.message || 'Failed to invite tenant.');
        },
    });

    const assignMembersMutation = useMutation({
        mutationFn: async ({ collaborationId, userIds }) => {
            const ids = Array.isArray(userIds) ? userIds : [];
            for (const userId of ids) {
                await inviteProjectCollaborationMember(projectId, { userId, collaborationId });
            }
        },
        onSuccess: () => {
            invalidateCollaboration();
            message.success('Member(s) assigned to collaboration.');
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || err?.message || 'Failed to assign members.');
        },
    });

    const acceptMutation = useMutation({
        mutationFn: (collaborationId) =>
            acceptProjectCollaboration(projectId, collaborationId ? { collaborationId } : {}),
        onSuccess: () => {
            invalidateCollaboration();
            message.success('Collaboration invite accepted.');
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || err?.message || 'Failed to accept invite.');
        },
    });

    const rejectMutation = useMutation({
        mutationFn: (collaborationId) =>
            rejectProjectCollaboration(projectId, collaborationId ? { collaborationId } : {}),
        onSuccess: () => {
            invalidateCollaboration();
            message.success('Collaboration invite rejected.');
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || err?.message || 'Failed to reject invite.');
        },
    });

    const openInviteTenantModal = useCallback(() => setInviteTenantModalOpen(true), []);
    const closeInviteTenantModal = useCallback(() => {
        setInviteTenantModalOpen(false);
        inviteTenantForm.resetFields();
    }, [inviteTenantForm]);

    const openAssignModal = useCallback(
        (row) => {
            setAssignTarget(row);
            setAssignModalOpen(true);
        },
        [],
    );

    const closeAssignModal = useCallback(() => {
        setAssignModalOpen(false);
        setAssignTarget(null);
        assignForm.resetFields();
    }, [assignForm]);

    const submitInviteTenant = useCallback(() => {
        inviteTenantForm.validateFields().then((values) => {
            const body = buildInviteTenantPayload(values);
            if (!body) {
                message.warning('Enter a tenant or company id');
                return;
            }
            inviteTenantMutation.mutate(body, { onSuccess: () => closeInviteTenantModal() });
        });
    }, [inviteTenantForm, inviteTenantMutation, closeInviteTenantModal]);

    const submitAssignMembers = useCallback(() => {
        if (!assignTarget?.id) return;
        assignForm.validateFields().then((values) => {
            const userIds = values?.userIds ?? [];
            if (!userIds.length) {
                message.warning('Select at least one user');
                return;
            }
            assignMembersMutation.mutate(
                { collaborationId: assignTarget.id, userIds },
                { onSuccess: () => closeAssignModal() },
            );
        });
    }, [assignForm, assignTarget, assignMembersMutation, closeAssignModal]);

    const acceptInvite = useCallback(
        (collaborationId) => acceptMutation.mutate(collaborationId),
        [acceptMutation],
    );

    const rejectInvite = useCallback(
        (collaborationId) => rejectMutation.mutate(collaborationId),
        [rejectMutation],
    );

    const isIncomingPending = useCallback(
        (row) =>
            row?.status === 'pending' &&
            currentTenantId &&
            row?.invitedTenantId &&
            String(row.invitedTenantId) === String(currentTenantId),
        [currentTenantId],
    );

    const assignSelectOptions = useMemo(() => {
        const existingIds = new Set(
            (assignTarget?.members ?? []).map((m) => String(m.id ?? m._id ?? m.userId ?? '')),
        );
        return usersList
            .filter((u) => {
                const uid = u.id ?? u._id;
                return uid && !existingIds.has(String(uid));
            })
            .map((u) => {
                const id = u.id ?? u._id;
                const displayName = (u.name && String(u.name).trim()) || u.email || String(id ?? '');
                const email = (u.email && String(u.email).trim()) || '';
                return {
                    value: id,
                    label: [displayName, email].filter(Boolean).join(' · '),
                    displayName,
                    email,
                };
            });
    }, [usersList, assignTarget]);

    const filterSelectOption = useCallback((input, option) => {
        const q = (input || '').trim().toLowerCase();
        if (!q) return true;
        const name = String(option?.displayName ?? '').toLowerCase();
        const email = String(option?.email ?? '').toLowerCase();
        return name.includes(q) || email.includes(q);
    }, []);

    const tableData = useMemo(
        () =>
            collaborations.map((row) => ({
                ...row,
                key: row.id ?? row.invitedTenantId,
                isIncomingPending: isIncomingPending(row),
            })),
        [collaborations, isIncomingPending],
    );

    const collaborationTableColumns = useMemo(
        () => [
            {
                title: 'Company / tenant',
                dataIndex: 'tenantName',
                ellipsis: true,
                render: (name) => name || '—',
            },
            {
                title: 'Status',
                dataIndex: 'status',
                width: 130,
                render: (status) => status || '—',
            },
            {
                title: 'Members',
                dataIndex: 'memberCount',
                width: 90,
                render: (count) => (count != null ? String(count) : '0'),
            },
            {
                title: 'Invited',
                dataIndex: 'invitedAt',
                width: 140,
                render: (d) => formatDate(d) ?? '—',
            },
        ],
        [],
    );

    return {
        canView,
        canInvite,
        canAssign,
        collaborations,
        tableData,
        collaborationTableColumns,
        isLoading,
        isError,
        refetch,
        inviteTenantModalOpen,
        openInviteTenantModal,
        closeInviteTenantModal,
        inviteTenantForm,
        submitInviteTenant,
        inviteTenantPending: inviteTenantMutation.isPending,
        assignModalOpen,
        assignTarget,
        openAssignModal,
        closeAssignModal,
        assignForm,
        assignSelectOptions,
        filterSelectOption,
        submitAssignMembers,
        assignMembersPending: assignMembersMutation.isPending,
        acceptInvite,
        rejectInvite,
        acceptPending: acceptMutation.isPending,
        rejectPending: rejectMutation.isPending,
        isIncomingPending,
    };
}
